'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import {
  createCleanorExportFileName,
  createPdfPreviewUrl,
  formatFileSize,
  isPdfFile,
  openPdfDocument,
  triggerBrowserDownload,
} from '@/lib/browser-pdf-tools';
import { consumeToolUsage, formatToolCreditSuccess } from '@/lib/browser-tool-usage';

type NumberingSource = {
  id: string;
  file: File;
  name: string;
  size: number;
  pageCount: number;
  previewUrl: string | null;
};

type NumberPosition =
  | 'top-left'
  | 'top-center'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-center'
  | 'bottom-right';
type NumberBadgeFill = 'white' | 'warm-beige' | 'soft-beige' | 'slate' | 'transparent';

const badgeFillOptions: Array<{
  id: NumberBadgeFill;
  label: string;
  hint: string;
}> = [
  {
    id: 'white',
    label: 'White',
    hint: 'Clean default for dense document pages.',
  },
  {
    id: 'warm-beige',
    label: 'Warm beige',
    hint: 'Softer on off-white scans and paper-like pages.',
  },
  {
    id: 'soft-beige',
    label: 'Soft beige',
    hint: 'A lighter cream tone for lower-contrast pages.',
  },
  {
    id: 'slate',
    label: 'Slate',
    hint: 'Darker circle with a light page number.',
  },
  {
    id: 'transparent',
    label: 'Transparent',
    hint: 'No circle, just the page number over the content.',
  },
];

async function readSource(file: File): Promise<NumberingSource> {
  const document = await openPdfDocument(new Uint8Array(await file.arrayBuffer()));

  try {
    return {
      id: crypto.randomUUID(),
      file,
      name: file.name,
      size: file.size,
      pageCount: document.numPages,
      previewUrl: await createPdfPreviewUrl(document),
    };
  } catch {
    throw new Error(`"${file.name}" could not be opened. Use a standard unlocked PDF file.`);
  } finally {
    document.cleanup?.();
    await document.destroy?.();
  }
}

function createExportFileName(pageCount: number) {
  const pageLabel = pageCount === 1 ? '1-page' : `${pageCount}-pages`;
  return createCleanorExportFileName('add-page-numbers', [pageLabel], 'pdf');
}

function getPositionLabel(position: NumberPosition) {
  switch (position) {
    case 'top-left':
      return 'Top left';
    case 'top-center':
      return 'Top center';
    case 'top-right':
      return 'Top right';
    case 'bottom-left':
      return 'Bottom left';
    case 'bottom-center':
      return 'Bottom center';
    default:
      return 'Bottom right';
  }
}

function getBadgeAppearance(fill: NumberBadgeFill) {
  switch (fill) {
    case 'warm-beige':
      return {
        fillColor: rgb(0.97, 0.93, 0.86),
        textColor: rgb(0.16, 0.18, 0.24),
        fillOpacity: 0.96,
      };
    case 'soft-beige':
      return {
        fillColor: rgb(0.95, 0.91, 0.8),
        textColor: rgb(0.16, 0.18, 0.24),
        fillOpacity: 0.94,
      };
    case 'slate':
      return {
        fillColor: rgb(0.19, 0.22, 0.31),
        textColor: rgb(1, 1, 1),
        fillOpacity: 0.92,
      };
    case 'transparent':
      return {
        fillColor: null,
        textColor: rgb(0.16, 0.18, 0.24),
        fillOpacity: 0,
      };
    default:
      return {
        fillColor: rgb(1, 1, 1),
        textColor: rgb(0.16, 0.18, 0.24),
        fillOpacity: 0.96,
      };
  }
}

export default function BrowserAddPageNumbersPdf() {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const downloadUrlRef = useRef<string | null>(null);
  const [source, setSource] = useState<NumberingSource | null>(null);
  const [startNumber, setStartNumber] = useState(1);
  const [position, setPosition] = useState<NumberPosition>('bottom-center');
  const [badgeFill, setBadgeFill] = useState<NumberBadgeFill>('white');
  const [fontSize, setFontSize] = useState(14);
  const [margin, setMargin] = useState(22);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);
  const [resultSize, setResultSize] = useState<number | null>(null);

  const previewExample = useMemo(
    () =>
      `${getPositionLabel(position)} • starts at ${startNumber} • ${
        badgeFillOptions.find((option) => option.id === badgeFill)?.label ?? 'White'
      }`,
    [badgeFill, position, startNumber],
  );

  useEffect(() => {
    return () => {
      if (source?.previewUrl) {
        URL.revokeObjectURL(source.previewUrl);
      }

      if (downloadUrlRef.current) {
        URL.revokeObjectURL(downloadUrlRef.current);
      }
    };
  }, [source]);

  function resetExportState() {
    if (downloadUrlRef.current) {
      URL.revokeObjectURL(downloadUrlRef.current);
    }

    downloadUrlRef.current = null;
    setResultSize(null);
  }

  function replaceSource(nextSource: NumberingSource | null) {
    setSource((current) => {
      if (current?.previewUrl) {
        URL.revokeObjectURL(current.previewUrl);
      }

      return nextSource;
    });
  }

  async function handleFileSelection(file: File | null) {
    setErrorMessage(null);
    setCreditMessage(null);
    resetExportState();

    if (!file) {
      replaceSource(null);
      return;
    }

    if (!isPdfFile(file)) {
      replaceSource(null);
      setErrorMessage('Choose one PDF file to number.');
      return;
    }

    try {
      replaceSource(await readSource(file));
    } catch (error) {
      replaceSource(null);
      setErrorMessage(error instanceof Error ? error.message : 'This PDF could not be opened.');
    }
  }

  function clearSource() {
    replaceSource(null);
    setErrorMessage(null);
    setCreditMessage(null);
    resetExportState();

    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }

  async function saveNumberedPdf() {
    if (!source) {
      setErrorMessage('Choose a PDF first.');
      return;
    }

    setIsSaving(true);
    setErrorMessage(null);
    setCreditMessage(null);

    try {
      const credits = await consumeToolUsage(
        'add-page-numbers',
        'Credits are not available for another page-number export right now.',
      );

      const document = await PDFDocument.load(await source.file.arrayBuffer());
      const font = await document.embedFont(StandardFonts.Helvetica);
      const badgeAppearance = getBadgeAppearance(badgeFill);

      document.getPages().forEach((page, index) => {
        const numberLabel = String(startNumber + index);
        const { width, height } = page.getSize();
        const textWidth = font.widthOfTextAtSize(numberLabel, fontSize);
        const radius = Math.max(fontSize * 0.95, textWidth / 2 + fontSize * 0.45);
        const centerX = position.endsWith('left')
          ? margin + radius
          : position.endsWith('center')
            ? width / 2
            : width - margin - radius;
        const centerY = position.startsWith('top') ? height - margin - radius : margin + radius;

        if (badgeAppearance.fillColor) {
          page.drawCircle({
            x: centerX,
            y: centerY,
            size: radius,
            color: badgeAppearance.fillColor,
            opacity: badgeAppearance.fillOpacity,
          });
        }

        page.drawText(numberLabel, {
          x: Math.max(12, centerX - textWidth / 2),
          y: Math.max(12, centerY - fontSize * 0.34),
          size: fontSize,
          font,
          color: badgeAppearance.textColor,
        });
      });

      const bytes = await document.save();
      const blob = new Blob([bytes.buffer as ArrayBuffer], { type: 'application/pdf' });
      const nextUrl = URL.createObjectURL(blob);
      downloadUrlRef.current = nextUrl;
      setResultSize(blob.size);
      triggerBrowserDownload(nextUrl, createExportFileName(source.pageCount));
      setCreditMessage(formatToolCreditSuccess('Page-number export', credits));
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'This PDF could not be updated.');
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <article className="tool-live-card tool-pdf-card">
      <div className="tool-live-header">
        <div>
          <h2>Add page numbers to one PDF</h2>
          <p>
            Number every page in the browser, choose where the numbering sits, and export one
            cleaned-up PDF right away.
          </p>
        </div>
        <span className="tool-live-badge">Browser-first</span>
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <button
            className={`tool-live-dropzone ${isDragActive ? 'tool-live-dropzone-active' : ''}`}
            onClick={() => inputRef.current?.click()}
            onDragEnter={() => setIsDragActive(true)}
            onDragLeave={() => setIsDragActive(false)}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragActive(true);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragActive(false);
              void handleFileSelection(event.dataTransfer.files?.[0] ?? null);
            }}
            type="button"
          >
            <div>
              <strong>
                {source ? 'PDF ready for numbering' : 'Choose one PDF or drop it here'}
              </strong>
              <span>
                {source
                  ? `${source.pageCount} pages • ${formatFileSize(source.size)}`
                  : 'Works best with reports, briefs, and scan bundles'}
              </span>
            </div>
          </button>
          <input
            ref={inputRef}
            accept="application/pdf,.pdf"
            className="tool-live-file-input"
            onChange={(event) => void handleFileSelection(event.target.files?.[0] ?? null)}
            type="file"
          />

          <div className="tool-live-controls tool-pdf-settings">
            <label className="tool-live-field">
              <span>Start number</span>
              <input
                min={1}
                onChange={(event) => setStartNumber(Math.max(1, Number(event.target.value) || 1))}
                type="number"
                value={startNumber}
              />
            </label>

            <label className="tool-live-field">
              <span>Position</span>
              <select
                onChange={(event) => setPosition(event.target.value as NumberPosition)}
                value={position}
              >
                <option value="bottom-center">Bottom center</option>
                <option value="bottom-right">Bottom right</option>
                <option value="bottom-left">Bottom left</option>
                <option value="top-center">Top center</option>
                <option value="top-right">Top right</option>
                <option value="top-left">Top left</option>
              </select>
            </label>

            <label className="tool-live-field">
              <span>Font size</span>
              <input
                max={28}
                min={10}
                onChange={(event) => setFontSize(Math.max(10, Number(event.target.value) || 10))}
                type="number"
                value={fontSize}
              />
            </label>

            <label className="tool-live-field">
              <span>Circle</span>
              <select
                onChange={(event) => setBadgeFill(event.target.value as NumberBadgeFill)}
                value={badgeFill}
              >
                {badgeFillOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="tool-live-field">
              <span>Margin</span>
              <input
                max={48}
                min={12}
                onChange={(event) => setMargin(Math.max(12, Number(event.target.value) || 12))}
                type="number"
                value={margin}
              />
            </label>
          </div>

          <div className="hero-actions tool-live-actions">
            <button
              className="button"
              disabled={!source || isSaving}
              onClick={() => void saveNumberedPdf()}
              type="button"
            >
              {isSaving ? 'Saving PDF…' : 'Add page numbers'}
            </button>
            {source ? (
              <button className="button button-ghost" onClick={clearSource} type="button">
                Clear file
              </button>
            ) : null}
          </div>

          {creditMessage ? (
            <p className="support-feedback support-feedback-success">{creditMessage}</p>
          ) : null}
          {errorMessage ? (
            <p className="support-feedback support-feedback-error">{errorMessage}</p>
          ) : null}
        </div>

        <aside className="tool-live-panel">
          <h3>Document finishing helper</h3>
          <div className="tool-mockup-chip-list">
            <span>PDF → PDF</span>
            <span>Page numbering</span>
            <span>{previewExample}</span>
            <span>Local export</span>
          </div>
          <p>
            Useful when the file is done but still needs readable numbering for review, print, or
            upload. The page number sits on top of the page content with an optional circle badge.
          </p>
          <div className="tool-live-panel-note">
            <strong>Best browser support</strong>
            <span>
              This tool overlays visible page numbers on standard unlocked PDFs without moving the
              file off-device.
            </span>
          </div>
          {source ? (
            <div className="tool-live-stats">
              <div>
                <strong>Pages</strong>
                <span>{source.pageCount}</span>
              </div>
              <div>
                <strong>Source size</strong>
                <span>{formatFileSize(source.size)}</span>
              </div>
              <div>
                <strong>Layout</strong>
                <span>{previewExample}</span>
              </div>
              <div>
                <strong>Circle style</strong>
                <span>
                  {badgeFillOptions.find((option) => option.id === badgeFill)?.label ?? 'White'}
                </span>
              </div>
              {resultSize ? (
                <div>
                  <strong>Last export</strong>
                  <span>{formatFileSize(resultSize)}</span>
                </div>
              ) : null}
              <div>
                <strong>Privacy</strong>
                <span>Processed in your browser</span>
              </div>
            </div>
          ) : null}
        </aside>
      </div>

      {source?.previewUrl ? (
        <div className="tool-live-preview">
          <h3>Source preview</h3>
          <div className="tool-live-preview-frame">
            <img alt="" src={source.previewUrl} />
          </div>
        </div>
      ) : null}
    </article>
  );
}
